import { Box, Checkbox, FormControlLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '../../../../store/storeAuth';
import { ThirdPartyIntegrationService } from '../../../../service/ThirdPartyIntegrationService';
import Label from '../../../UI/Label/Label';

interface SchemaProperty {
  id: string;
  name: string;
  type: string;
  options?: Array<{ id: string; name: string }>;
}

const READ_ONLY_TYPES = new Set(['formula', 'rollup', 'created_time', 'created_by', 'last_edited_time', 'last_edited_by']);

interface NotionPropertiesEditorProps {
  databaseOrRecordId: string;
  isRecordId: boolean;
  value: Record<string, unknown>;
  onChange: (value: Record<string, unknown>) => void;
  disabled?: boolean;
  nodeData?: Record<string, unknown>;
}

export const NotionPropertiesEditor: React.FC<NotionPropertiesEditorProps> = ({
  databaseOrRecordId,
  isRecordId,
  value,
  onChange,
  disabled,
  nodeData,
}) => {
  const { user } = useAuthStore(state => state);
  const isDark = user?.theme === 'dark';
  const [schema, setSchema] = useState<SchemaProperty[]>([]);
  const [loading, setLoading] = useState(false);

  const context: Record<string, unknown> = {};
  if (nodeData?.integrationId) context.integrationId = nodeData.integrationId;
  if (nodeData?.connectedByUserId) context.connectedByUserId = nodeData.connectedByUserId;
  if (isRecordId) context.isRecordId = true;

  useEffect(() => {
    if (!databaseOrRecordId || disabled) return;
    let cancelled = false;
    setLoading(true);
    ThirdPartyIntegrationService.getDatabaseSchema('notion', databaseOrRecordId, context)
      .then(res => {
        if (!cancelled && res?.data?.properties) {
          setSchema(res.data.properties.filter(
            p => !READ_ONLY_TYPES.has(p.type),
          ));
        }
      })
      .catch(() => {
        if (!cancelled) setSchema([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [databaseOrRecordId, isRecordId, disabled, nodeData?.integrationId, nodeData?.connectedByUserId]);

  const updateOne = useCallback(
    (propertyId: string, val: unknown) => {
      onChange({ ...value, [propertyId]: val });
    },
    [value, onChange],
  );

  if (!databaseOrRecordId) return null;
  if (loading) return <Typography variant="caption">Loading properties...</Typography>;

  const inputSx = {
    width: '100%',
    maxWidth: 360,
    '& .MuiOutlinedInput-root': { bgcolor: isDark ? '#101828' : '#F9FAFB' },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
      <Label
        labelName="Properties"
        isAdvanced
        labelStyle={{
          fontSize: '14px',
          color: isDark ? '#D0D5DD' : '#101828',
          fontWeight: 500,
        }}
      />
      {schema.map(prop => {
        const current = value[prop.id] ?? value[prop.name];
        if (prop.type === 'title') {
          return (
            <TextField
              key={prop.id}
              size="small"
              label={prop.name}
              value={current ?? ''}
              onChange={e => updateOne(prop.id, e.target.value)}
              disabled={disabled}
              sx={inputSx}
            />
          );
        }
        if (prop.type === 'checkbox') {
          return (
            <FormControlLabel
              key={prop.id}
              control={
                <Checkbox
                  checked={current === true || current === 'true'}
                  onChange={e => updateOne(prop.id, e.target.checked)}
                  disabled={disabled}
                  size="small"
                />
              }
              label={prop.name}
              sx={{ color: isDark ? '#D0D5DD' : '#344054', '& .MuiFormControlLabel-label': { fontSize: '13px' } }}
            />
          );
        }
        if (prop.type === 'number') {
          return (
            <TextField
              key={prop.id}
              size="small"
              label={prop.name}
              type="number"
              value={current ?? ''}
              onChange={e => {
                const v = e.target.value;
                const num = v === '' ? undefined : Number(v);
                updateOne(prop.id, Number.isFinite(num) ? num : v);
              }}
              disabled={disabled}
              sx={inputSx}
            />
          );
        }
        if (prop.type === 'date') {
          return (
            <TextField
              key={prop.id}
              size="small"
              label={prop.name}
              type="datetime-local"
              value={typeof current === 'string' && current.length >= 16 ? current.slice(0, 16) : ''}
              onChange={e => updateOne(prop.id, e.target.value ? `${e.target.value}:00.000Z` : '')}
              disabled={disabled}
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
          );
        }
        if ((prop.type === 'select' || prop.type === 'status') && prop.options?.length) {
          return (
            <Select
              key={prop.id}
              size="small"
              value={current ?? ''}
              onChange={e => updateOne(prop.id, e.target.value)}
              disabled={disabled}
              displayEmpty
              sx={{ ...inputSx, minWidth: 200 }}
              renderValue={v => v || `Select ${prop.name}`}
            >
              {prop.options.map(opt => (
                <MenuItem key={opt.id} value={opt.name}>
                  {opt.name}
                </MenuItem>
              ))}
            </Select>
          );
        }
        if (prop.type === 'multi_select' && prop.options?.length) {
          const arr = Array.isArray(current) ? current : typeof current === 'string' ? (current ? [current] : []) : [];
          return (
            <Select
              key={prop.id}
              size="small"
              multiple
              value={arr}
              onChange={e => updateOne(prop.id, e.target.value)}
              disabled={disabled}
              displayEmpty
              sx={{ ...inputSx, minWidth: 200 }}
              renderValue={v => (Array.isArray(v) && v.length ? v.join(', ') : `Select ${prop.name}`)}
            >
              {prop.options.map(opt => (
                <MenuItem key={opt.id} value={opt.name}>
                  {opt.name}
                </MenuItem>
              ))}
            </Select>
          );
        }
        return (
          <TextField
            key={prop.id}
            size="small"
            label={prop.name}
            value={current ?? ''}
            onChange={e => updateOne(prop.id, e.target.value)}
            disabled={disabled}
            multiline={prop.type === 'rich_text'}
            sx={inputSx}
          />
        );
      })}
      {schema.length === 0 && !loading && (
        <Typography variant="caption" color="text.secondary">
          No writable properties or schema could not be loaded.
        </Typography>
      )}
    </Box>
  );
};
